
import { getDay, getHours, format, startOfWeek, differenceInMinutes, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

/**
 * Calculates night minutes for a shift
 */
export const calculateNightMinutes = (startDate: Date, endDate: Date): number => {
  let currentDate = new Date(startDate);
  let totalNightMinutes = 0;
  
  while (currentDate < endDate) {
    const hour = getHours(currentDate);
    const isNightHour = hour >= 22 || hour < 6;
    
    if (isNightHour) {
      totalNightMinutes += 1;
    }
    
    currentDate = new Date(currentDate.getTime() + 60000); // Add 1 minute
    
    if (totalNightMinutes > 24 * 60) {
      console.error("Night hours calculation exceeded 24 hours, stopping to prevent infinite loop");
      break;
    }
  }
  
  return totalNightMinutes;
};

/**
 * Checks if a date falls on a Sunday
 */
export const isDateOnSunday = (date: Date): boolean => {
  return getDay(date) === 0;
};

/**
 * Checks if a date is in a holiday period (either a Sunday or a listed holiday)
 */
export const isInHolidayPeriod = (date: Date, holidays: string[]): boolean => {
  const currentDay = format(date, 'yyyy-MM-dd');
  return isDateOnSunday(date) || (holidays && holidays.includes(currentDay));
};

/**
 * Calculates holiday minutes for a shift
 */
export const calculateHolidayMinutes = (startDate: Date, endDate: Date, holidays: string[] = []): number => {
  let currentDate = new Date(startDate);
  let totalHolidayMinutes = 0;
  
  while (currentDate < endDate) {
    if (isInHolidayPeriod(currentDate, holidays)) {
      totalHolidayMinutes += 1;
    }
    
    currentDate = new Date(currentDate.getTime() + 60000); // Add 1 minute
    
    if (totalHolidayMinutes > 24 * 60 * 7) {
      console.error("Holiday hours calculation exceeded 1 week, stopping to prevent infinite loop");
      break;
    }
  }
  
  return totalHolidayMinutes;
};

/**
 * Determines if a week's overtime should be included in the current month based on end of month
 * If month ends on Sunday (0), include in current month
 * If month ends on any other day, include in next month
 */
export const shouldIncludeWeekOvertime = (lastDayOfMonth: Date): boolean => {
  const dayOfWeek = getDay(lastDayOfMonth);
  
  console.log(`Month ends on day ${dayOfWeek} (0=Sunday, 1=Monday, etc)`);
  
  // If month ends on Sunday (0), include overtime in current month
  if (dayOfWeek === 0) {
    console.log('Month ends on Sunday, including overtime in current month');
    return true;
  }
  
  // If month ends on Monday through Saturday (1-6), include overtime in next month
  console.log('Month ends on a day other than Sunday, including overtime in next month');
  return false;
};

/**
 * Get date ranges for queries (month and year)
 */
export const getDateRanges = (month: Date) => {
  // Get start and end of selected month for work statistics
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  
  // Get start and end of current year for leave statistics
  const yearStart = startOfYear(month);
  const yearEnd = endOfYear(month);
  
  const formattedMonthStart = format(monthStart, 'yyyy-MM-dd');
  const formattedMonthEnd = format(monthEnd, 'yyyy-MM-dd');
  const formattedYearStart = format(yearStart, 'yyyy-MM-dd');
  const formattedYearEnd = format(yearEnd, 'yyyy-MM-dd');

  return {
    monthStart,
    monthEnd,
    yearStart,
    yearEnd,
    formattedMonthStart,
    formattedMonthEnd,
    formattedYearStart,
    formattedYearEnd
  };
};
